// frontend/src/components/ForgotPassword.jsx
// Password reset flow with security questions and self-service email reset
//
// Flow:
// Step 1: Enter username
// Step 1.5: If no security questions, show self-service email reset option
// Step 2: Answer security questions (if available)
// Step 3: Set new password (after successful verification)

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../axiosConfig';
import { useNavigate, Link } from 'react-router-dom';
import '../index.css';
import { validatePassword } from '../utils/passwordValidator';

function ForgotPassword() {
  // Step states:
  // 1 = Enter username
  // 1.5 = Self-service email reset (no security questions)
  // 2 = Answer security questions
  // 3 = Enter new password
  // 4 = Email reset link sent (success screen)
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [challengeId, setChallengeId] = useState(null);
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Step 1: Verify username and get security questions
  const handleUsernameSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        '/api/password-reset/initiate',
        { username },
        { skipAuthSessionHandling: true }
      );

      if (response.data.challengeId && Array.isArray(response.data.questions)) {
        // Uniform challenge response; questions are public i18n keys, while
        // answer payloads are keyed by opaque per-challenge prompt IDs.
        setChallengeId(response.data.challengeId);
        setSecurityQuestions(response.data.questions);
        setStep(2);
      } else {
        // No security questions - offer self-service email reset
        // Note: This response is the same for invalid usernames (prevents enumeration)
        setStep(1.5);
      }
    } catch (error) {
      if (error.response?.status === 429) {
        setErrorMessage(t('tooManyAttempts'));
      } else {
        setErrorMessage(error.response?.data?.error || t('errorOccurred'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 1.5: Send reset link via email (self-service)
  const handleSendResetLink = async () => {
    setErrorMessage('');
    setIsLoading(true);

    try {
      await axios.post(
        '/api/password-reset/send-link',
        { username },
        { skipAuthSessionHandling: true }
      );
      // Always show success (prevents enumeration)
      setStep(4);
    } catch (error) {
      if (error.response?.status === 429) {
        setErrorMessage(t('tooManyAttempts'));
      } else {
        // Still show success to prevent enumeration
        setStep(4);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Verify security answers
  const handleSecurityAnswersSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    // Validate all questions are answered
    const answeredQuestions = Object.keys(answers).filter(key => answers[key]?.trim());
    if (answeredQuestions.length < securityQuestions.length) {
      setErrorMessage(t('pleaseAnswerAllQuestions'));
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        '/api/password-reset/verify',
        { challengeId, answers },
        { skipAuthSessionHandling: true }
      );

      if (response.data.success) {
        setResetToken(response.data.resetToken);
        setStep(3);
      } else {
        setErrorMessage(t('incorrectSecurityAnswers'));
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || t('verificationFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset password
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    // Validate password
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      setErrorMessage(passwordValidation.message);
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMessage(t('passwordsDoNotMatch'));
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        '/api/password-reset/complete',
        { resetToken, newPassword },
        { skipAuthSessionHandling: true }
      );

      if (response.data.success) {
        setSuccessMessage(t('passwordResetSuccess'));
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || t('passwordResetFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const getQuestionLabel = (question) => {
    if (question?.i18nKey) {
      return t(question.i18nKey);
    }
    return question?.text || '';
  };

  return (
    <div className="max-w-md mx-auto mt-8 p-6 bg-gray-100 dark:bg-gray-800 shadow-md rounded-lg">
      <h2 className="text-2xl font-bold mb-4 text-center text-blue-600 dark:text-blue-400">
        {t('resetPassword')}
      </h2>

      {/* Step 1: Username */}
      {step === 1 && (
        <form onSubmit={handleUsernameSubmit}>
          <div className="mb-4">
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('username')}:
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              placeholder={t('enterYourUsername')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? t('loading') : t('continue')}
          </button>
        </form>
      )}

      {/* Step 1.5: Self-service email reset (no security questions) */}
      {step === 1.5 && (
        <div>
          <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              {t('noSecurityQuestionsSetSelfService')}
            </p>
          </div>

          <button
            type="button"
            onClick={handleSendResetLink}
            disabled={isLoading}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
          >
            {isLoading ? t('loading') : t('sendResetLink')}
          </button>

          <button
            type="button"
            onClick={() => {
              setStep(1);
              setUsername('');
              setErrorMessage('');
            }}
            className="w-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 transition duration-200"
          >
            {t('tryDifferentUsername')}
          </button>
        </div>
      )}

      {/* Step 2: Security Questions */}
      {step === 2 && (
        <form onSubmit={handleSecurityAnswersSubmit}>
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {t('answerSecurityQuestions')}
          </p>

          {securityQuestions.map((question) => (
            <div key={question.id} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {getQuestionLabel(question)}
              </label>
              <input
                type="text"
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          ))}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
          >
            {isLoading ? t('loading') : t('verifyAnswers')}
          </button>

          <button
            type="button"
            onClick={handleSendResetLink}
            disabled={isLoading}
            className="w-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('sendResetLink')}
          </button>

        </form>
      )}

      {/* Step 3: New Password */}
      {step === 3 && (
        <form onSubmit={handlePasswordReset}>
          <div className="mb-4">
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('newPassword')}:
            </label>
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
              {t('passwordRequirements')}
            </p>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
                tabIndex="-1"
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-500">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('confirmPassword')}:
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? t('loading') : t('resetPassword')}
          </button>
        </form>
      )}

      {/* Step 4: Email sent success */}
      {step === 4 && (
        <div>
          <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg">
            <div className="flex items-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 text-green-600 dark:text-green-400 mr-2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-green-800 dark:text-green-200 font-medium">
                {t('emailSent')}
              </span>
            </div>
            <p className="text-green-700 dark:text-green-300 text-sm">
              {t('resetLinkSentGeneric')}
            </p>
          </div>

          <Link
            to="/login"
            className="block w-full text-center bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
          >
            {t('backToLogin')}
          </Link>
        </div>
      )}

      {/* Messages */}
      {errorMessage && (
        <div className="mt-4 p-3 bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 rounded">
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div className="mt-4 p-3 bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-200 rounded">
          {successMessage}
        </div>
      )}

      {/* Back to login link */}
      {step !== 4 && (
        <div className="mt-4 text-center">
          <Link
            to="/login"
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            {t('backToLogin')}
          </Link>
        </div>
      )}
    </div>
  );
}

export default ForgotPassword;
